import { Component, input, inject, effect, signal, computed } from '@angular/core';
import {
  PackageForAdminResponse,
  PackagesClient, SetUserPackageStatusCommand,
  UserPackageStatus
} from '../../../infrastructure/shared/model/fss.api';
import { firstValueFrom } from 'rxjs';
import { ButtonComponent, ButtonSeverity, ButtonSize } from '../../../infrastructure/shared/ui/button/button';
import { Arrow } from '../../../infrastructure/shared/icons/arrow/arrow';
import { Building05 } from '../../../infrastructure/shared/icons/building-05/building-05';
import { Hash02 } from '../../../infrastructure/shared/icons/hash-02/hash-02';
import { UserCircle } from '../../../infrastructure/shared/icons/user-circle/user-circle';
import { Mail02 } from '../../../infrastructure/shared/icons/mail-02/mail-02';
import { Phone } from '../../../infrastructure/shared/icons/phone/phone';
import { Chip } from 'primeng/chip';
import { LocationAdminService } from '../../../infrastructure/shared/services/location-admin.service';
import { Send01 } from '../../../infrastructure/shared/icons/send-01/send-01';
import { Cube01 } from '../../../infrastructure/shared/icons/cube-01/cube-01';
import { getDateFormatedWithTime, getDateFormattedShort } from '../../../infrastructure/shared/services/date.service';
import { StepSuccess } from '../../../infrastructure/shared/icons/step-success/step-success';
import { Badge } from 'primeng/badge';
import { MarkerPin01 } from '../../../infrastructure/shared/icons/marker-pin-01/marker-pin-01';
import { HomeLine } from '../../../infrastructure/shared/icons/home-line/home-line';
import { Map01 } from '../../../infrastructure/shared/icons/map-01/map-01';
import { CheckCircle } from '../../../infrastructure/shared/icons/check-circle/check-circle';
import { SlashCircle01 } from '../../../infrastructure/shared/icons/slash-circle-01/slash-circle-01';
import { Check } from '../../../infrastructure/shared/icons/check/check';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { InvoiceSentDialog } from '../admin-packages-table/invoice-sent-dialog/invoice-sent-dialog';
import { ConfirmPaymentDialog } from '../../../infrastructure/shared/components/confirm-payment-dialog/confirm-payment-dialog';
import { ConfirmActionDialog } from '../../../infrastructure/shared/components/confirm-action-dialog/confirm-action-dialog';

@Component({
  standalone: true,
  selector: 'app-admin-package-details',
  imports: [
    ButtonComponent,
    Arrow,
    Building05,
    Hash02,
    UserCircle,
    Chip,
    Mail02,
    Phone,
    Send01,
    Cube01,
    StepSuccess,
    Badge,
    MarkerPin01,
    HomeLine,
    Map01,
    CheckCircle,
    SlashCircle01,
    Check,
    InvoiceSentDialog,
    ConfirmPaymentDialog,
    ConfirmActionDialog,
  ],
  templateUrl: './admin-package-details.html',
  styleUrl: './admin-package-details.scss',
})
export class AdminPackageDetails {
  /** Injections */
  private _client = inject(PackagesClient);
  private _location = inject(LocationAdminService);
  private _loader = inject(LoaderService);
  private _alert = inject(AlertService);

  id = input<string>(); //:id param from the route
  data = signal<PackageForAdminResponse | undefined>(undefined);

  /** Expired/extended packages have nothing left to do, so the "Akcije" panel is hidden. */
  hasActions = computed(() => {
    const status = this.data()?.status?.value;
    return status !== UserPackageStatus.EXPIRED && status !== UserPackageStatus.EXTENDED;
  });

  /** Invoice dialog */
  showInvoiceDialog = signal(false);
  selectedCommand = signal<SetUserPackageStatusCommand | undefined>(undefined);

  /** Confirm payment dialog */
  showConfirmPaymentDialog = signal(false);

  /** Cancel (reject) request confirmation dialog */
  showCancelDialog = signal(false);

  badgeStyle = {
    border: {
      radius: '8px',
    },
    secondary: {
      color: '#414651',
      background: '#FFFFFF',
    },
  };

  constructor() {
    effect(() => {
      let packageId = this.id();
      if (packageId) {
        this._load();
      }
    });
  }

  private _load() {
    this._getData(this.id()!).then((res) => {
      this.data.set(res.result);
    });
  }

  routeToPackagePackages() {
    this._location.routeToPackages();
  }

  getStatusChipClass(status: UserPackageStatus): string {
    switch (status) {
      case UserPackageStatus.NEW:
        return 'info md';
      case UserPackageStatus.INVOICE_SENT:
        return 'warn md';
      case UserPackageStatus.ACTIVE:
      case UserPackageStatus.EXTENDED:
        return 'success md';
      case UserPackageStatus.EXPIRED:
        return 'gray md';
      default:
        return 'red md';
    }
  }

  confirmCancel() {
    this.showCancelDialog.set(false);
    this.cancel();
  }

  cancel() {
    let req = {
      id: this.id(),
      status: UserPackageStatus.CANCELLED,
    } as SetUserPackageStatusCommand;

    this._loader.setLoadingText = 'Otkazivanje...';
    this._loader.setShowLoader = true;
    this._setStatus(req).then((res) => {
      this._load();
      this._alert.addSuccessMsg(res.result);
    });
  }

  setAsPaid() {
    let req = {
      id: this.id(),
      status: UserPackageStatus.ACTIVE,
    } as SetUserPackageStatusCommand;

    this._loader.setLoadingText = 'Čuvanje u toku...';
    this._loader.setShowLoader = true;
    this._setStatus(req).then((res) => {
      this.showConfirmPaymentDialog.set(false);
      this._load();
      this._alert.addSuccessMsg(res.result);
    });
  }

  setInvoiceSent(req: SetUserPackageStatusCommand) {
    this._loader.setLoadingText = 'Slanje fakture...';
    this._loader.setShowLoader = true;
    this._setStatus(req).then((res) => {
      this.showInvoiceDialog.set(false);
      this._load();
      this._alert.addSuccessMsg(res.result);
    });
  }

  openInvoiceDialog() {
    let req = {
      id: this.id(),
      status: UserPackageStatus.INVOICE_SENT,
      packagePrice: {
        price: this.data()!.price,
        originalPrice: this.data()!.price,
      },
    } as SetUserPackageStatusCommand;

    this.selectedCommand.set(req);
    this.showInvoiceDialog.set(true);
  }

  private async _getData(id: string) {
    return await firstValueFrom(this._client.getPackageForAdmin(id));
  }

  private async _setStatus(req: SetUserPackageStatusCommand) {
    return await firstValueFrom(this._client.setStatus(req));
  }

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
  protected readonly UserPackageStatus = UserPackageStatus;
  protected readonly getDateFormatedWithTime = getDateFormatedWithTime;
  protected readonly getDateFormattedShort = getDateFormattedShort;
}
